// ─── React and Hook Form ────────────────────────────────────────────────
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

// ─── Translations ───────────────────────────────────────────────────────
import { useTranslation } from "react-i18next";

// ─── UI Components ──────────────────────────────────────────────────────
import PrimaryButton from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import { ToastContainer, toast } from "react-toastify";

// ─── Styles and Utils ───────────────────────────────────────────────────
import classNames from "classnames";
import styles from "./AddOrganisation.module.scss";

interface Props {
  closeModal: () => void;
  password?: string;
}

// ─── StepThree Component ────────────────────────────────────────────────
const StepThree = ({ closeModal, password }: Props) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();

  // ─── Extracted Values from Form ───────────────────────────────────────
  const formValues = getValues();
  const { email } = formValues;

  // ─── Clipboard Copy Handler ───────────────────────────────────────────
  const handleCopyToClipboard = useCallback(() => {
    const SITE_URL = "https://admin.waffyapp.com/";
    const textToCopy = `البريد الإلكتروني\n(${email})\n\nكلمة المرور\n${password}\n\nرابط الموقع\n[${SITE_URL}](${SITE_URL})`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        toast(t("organization_saved_successfully"));
        closeModal();
      })
      .catch(() => {
        toast("Error");
      });
  }, [closeModal, email, password, t]);

  return (
    <div className={classNames(styles.step_container, styles["col_center"])}>
      {/* ─── Success Image and Title ────────────────────────────────────── */}
      <img
        className={styles["success_image"]}
        src="/assets/images/svg/tick.svg"
        alt="success"
      />
      <h1>{t("organization_success_title")}</h1>
      {password && (
        <>
          <p className={styles["success_subtitle"]}>
            {t("organization_success_description")}
          </p>

          {/* ─── Success Info Section ───────────────────────────────────────── */}
          <div className={styles["success_message_container"]}>
            {/* ─── Email Display ────────────────────────────────────────────── */}
            <div className={styles.email_container}>
              <div className={styles["image_container"]}>
                <img src="/assets/images/svg/mail.svg" alt="" />
              </div>
              <div className={styles["text_container"]}>
                <p className={styles["text_header"]}>{t("email")}</p>
                <p className={styles["subtitle5"]}>{email}</p>
              </div>
            </div>

            <div className={styles["line"]} />

            {/* ─── Password Display ─────────────────────────────────────────── */}
            <div className={styles.email_container}>
              <div className={styles["image_container"]}>
                <img src="/assets/images/svg/lock_icon.svg" alt="" />
              </div>
              <div className={styles["text_container"]}>
                <p className={styles["text_header"]}>{t("password")}</p>
                <p className={styles["subtitle5"]}>{password}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Action Buttons ────────────────────────────────────────────── */}
      <div className={styles["button_container"]}>
        {password && (
          <PrimaryButton
            onClick={handleCopyToClipboard}
            Lefticon={<img src="/assets/images/svg/copy.svg" alt="copy" />}>
            {t("copy_information")}
          </PrimaryButton>
        )}
        <OutlinedButton onClick={closeModal}>{t("close")}</OutlinedButton>
      </div>
      <ToastContainer />
    </div>
  );
};

export default StepThree;
